import React, { useState, useEffect, useRef } from 'react';
import { Upload, FileText, BookOpen, CheckCircle, AlertCircle, Trash2, Eye, Plus, Link, Video, File } from 'lucide-react';
import { ragService } from '../ia/knowledge-base/ragService';
import { lerToken } from '../services/sessao';
import './SaasLizTrainingHub.css';

interface DocumentItem {
  id: string;
  title: string;
  category: 'law' | 'education' | 'financial' | 'tech' | 'marketing' | 'sistema' | 'document_models';
  sourceTitle: string;
  sourceUrl: string;
  status: 'approved' | 'pending' | 'rejected';
  verifiedBy: string;
  verifiedAt: string;
  keywords: string[];
  summary: string;
}

type UploadTab = 'contrato' | 'texto' | 'arquivo' | 'link' | 'video';

const categoryLabels: Record<string, string> = {
  law: '⚖️ Legislação',
  education: '🎓 Educação',
  financial: '💰 Financeiro',
  tech: '🔧 Tecnologia',
  marketing: '📊 Marketing',
  sistema: '🖥️ Sistema',
  document_models: '📄 Modelos de Documentos',
};

const ACCEPTED_FILES = '.pdf,.doc,.docx,.ppt,.pptx,.txt,.md,.mp4,.avi,.mov,.mkv,.mp3,.wav,.m4a,.ogg,.webm,.aac,.flac';
// Formatos que a transcrição (Whisper via Groq) aceita diretamente.
const TRANSCRICAO_EXTS = ['mp3', 'wav', 'm4a', 'ogg', 'webm', 'aac', 'flac', 'mp4', 'mpeg', 'mpga'];
// Limite prático de tamanho para a API de transcrição do Groq (~25 MB por arquivo).
const MAX_TRANSCRICAO_MB = 25;
const MAX_FILE_MB = 100;

export default function SaasLizTrainingHub() {
  const [documents, setDocuments] = useState<DocumentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState<{ total: number; byCategory: Record<string, number>; official: number }>({ total: 0, byCategory: {}, official: 0 });
  const [selectedDoc, setSelectedDoc] = useState<DocumentItem | null>(null);
  const [filterCategory, setFilterCategory] = useState<string>('todos');
  const [searchTerm, setSearchTerm] = useState('');

  // ── Painel de adição (tabs) ─────────────────────────────────
  const [showAddPanel, setShowAddPanel] = useState(false);
  const [activeTab, setActiveTab] = useState<UploadTab>('arquivo');

  // ── Formulário texto (RAG) ──────────────────────────────────
  const [uploadForm, setUploadForm] = useState({
    title: '',
    summary: '',
    sourceTitle: '',
    sourceUrl: '',
    category: 'law' as const,
    keywords: '',
  });

  // ── Formulário arquivo ──────────────────────────────────────
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileForm, setFileForm] = useState({
    titulo: '',
    descricao: '',
    tipo_material: 'treinamento',
    audiencia: 'interno',
    categoria: 'education',
    palavras_chave: '',
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState('');

  // ── Formulário link ─────────────────────────────────────────
  const [linkForm, setLinkForm] = useState({
    titulo: '',
    url_link: '',
    descricao: '',
    categoria: 'education',
  });

  // ── Formulário vídeo ────────────────────────────────────────
  const [videoForm, setVideoForm] = useState({
    titulo: '',
    url_video: '',
    descricao: '',
    categoria: 'education',
  });

  // ── Modelo de Contrato (fluxo dedicado) ─────────────────────
  const [contratoForm, setContratoForm] = useState({
    titulo: '',
    descricao: '',
    texto: '',
    palavrasChave: '',
  });
  const [salvandoContrato, setSalvandoContrato] = useState(false);

  const handleAdicionarModeloContrato = async () => {
    if (!contratoForm.titulo.trim() || !contratoForm.texto.trim()) {
      setError('Informe o título e o texto completo do modelo de contrato.');
      return;
    }
    setSalvandoContrato(true);
    setError('');
    try {
      const resp = await fetch('/api/ia/knowledge-base/contract-model', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({
          titulo: contratoForm.titulo,
          descricao: contratoForm.descricao,
          texto: contratoForm.texto,
          palavrasChave: contratoForm.palavrasChave,
        }),
      });
      if (resp.status === 401 || resp.status === 403) {
        setError('Sua sessão expirou. Faça login novamente para cadastrar o modelo.');
        return;
      }
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      setUploadSuccess(`✅ Modelo "${contratoForm.titulo}" adicionado à base e já disponível para a Liz!`);
      setContratoForm({ titulo: '', descricao: '', texto: '', palavrasChave: '' });
      setShowAddPanel(false);
      loadDocuments();
      loadStats();
      setTimeout(() => setUploadSuccess(''), 5000);
    } catch {
      setError('Não foi possível salvar o modelo de contrato. Tente novamente.');
    } finally {
      setSalvandoContrato(false);
    }
  };

  // ── Status dos provedores de IA (créditos/saúde) ────────────
  interface ProvedorStatus {
    id: string;
    nome: string;
    papel: string;
    status: 'ok' | 'depleted' | 'error' | 'not_configured';
    detalhe: string;
    recargaUrl: string;
  }
  const [provedores, setProvedores] = useState<ProvedorStatus[]>([]);
  const [verificandoProvedores, setVerificandoProvedores] = useState(false);
  const [provedoresVerificadoEm, setProvedoresVerificadoEm] = useState<string>('');

  const verificarProvedores = async () => {
    setVerificandoProvedores(true);
    try {
      const resp = await fetch('/api/ia/providers/status', {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!resp.ok) return;
      const data = await resp.json();
      setProvedores(data.provedores || []);
      setProvedoresVerificadoEm(data.verificadoEm || '');
    } catch {
      /* silencioso — o card mostra o estado anterior */
    } finally {
      setVerificandoProvedores(false);
    }
  };

  // ── Dossiê do Sistema (o que a Liz sabe sobre o próprio produto) ──
  interface DossieSistema {
    disponivel: boolean;
    versao: string | null;
    geradoEm: string | null;
    ultimaEntregaEm: string | null;
    publicadoEm: string | null;
    modulos: Array<{ pasta: string; nome: string }>;
    entregas: Array<{ data: string; tipo: string; area: string; titulo: string; pr: number | null }>;
    notas: string;
  }
  const [dossie, setDossie] = useState<DossieSistema | null>(null);
  const [notasDossie, setNotasDossie] = useState('');
  const [dossieOcupado, setDossieOcupado] = useState(false);
  const [dossieMsg, setDossieMsg] = useState('');

  const carregarDossie = async () => {
    try {
      const resp = await fetch('/api/ia/knowledge-base/dossie', {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!resp.ok) return;
      const data: DossieSistema = await resp.json();
      setDossie(data);
      setNotasDossie(data.notas || '');
    } catch {
      /* silencioso — o card some se não carregar */
    }
  };

  const salvarNotasDossie = async () => {
    setDossieOcupado(true);
    setDossieMsg('');
    try {
      const resp = await fetch('/api/ia/knowledge-base/dossie/notas', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ notas: notasDossie }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      setDossieMsg('✅ Notas salvas — a Liz já responde com elas.');
      carregarDossie();
    } catch {
      setDossieMsg('❌ Não consegui salvar as notas. Tente novamente.');
    } finally {
      setDossieOcupado(false);
    }
  };

  const ressincronizarDossie = async () => {
    setDossieOcupado(true);
    setDossieMsg('');
    try {
      const resp = await fetch('/api/ia/knowledge-base/dossie/sincronizar', {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const r = await resp.json();
      setDossieMsg(`✅ Dossiê ${r.acao} (versão ${r.versao}) e reindexado.`);
      carregarDossie();
    } catch {
      setDossieMsg('❌ Falha ao republicar o dossiê.');
    } finally {
      setDossieOcupado(false);
    }
  };

  useEffect(() => {
    loadDocuments();
    loadStats();
    verificarProvedores();
    carregarDossie();
  }, []);

  const loadDocuments = async () => {
    setLoading(true);
    try {
      const docs = await ragService.listDocuments();
      setDocuments(docs as DocumentItem[]);
      setError('');
    } catch (err) {
      setError('Erro ao carregar documentos');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const report = await ragService.generateReport();
      setStats({
        total: report.totalDocuments,
        byCategory: report.byCategory,
        official: report.officialSources,
      });
    } catch (err) {
      console.error('Erro ao carregar estatísticas:', err);
    }
  };

  const getToken = () =>
    lerToken('saas') || '';

  // ── Upload de arquivo ───────────────────────────────────────
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    if (!file) return;
    if (file.size > MAX_FILE_MB * 1024 * 1024) {
      setError(`Arquivo muito grande. Máximo permitido: ${MAX_FILE_MB} MB`);
      return;
    }
    setSelectedFile(file);
    if (!fileForm.titulo) {
      setFileForm(f => ({ ...f, titulo: file.name.replace(/\.[^.]+$/, '') }));
    }
  };

  const handleFileDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0] ?? null;
    if (!file) return;
    if (file.size > MAX_FILE_MB * 1024 * 1024) {
      setError(`Arquivo muito grande. Máximo permitido: ${MAX_FILE_MB} MB`);
      return;
    }
    setSelectedFile(file);
    if (!fileForm.titulo) {
      setFileForm(f => ({ ...f, titulo: file.name.replace(/\.[^.]+$/, '') }));
    }
  };

  const handleUploadArquivo = async () => {
    if (!selectedFile) { setError('Selecione um arquivo'); return; }
    if (!fileForm.titulo.trim()) { setError('Informe um título'); return; }

    setError('');
    setUploadProgress(0);
    setUploadSuccess('');

    const ext = selectedFile.name.split('.').pop()?.toLowerCase() || '';
    const ehDocumentoTexto = ['pdf', 'docx', 'txt', 'md', 'markdown'].includes(ext);
    const ehTranscrivel = TRANSCRICAO_EXTS.includes(ext);

    if (ehTranscrivel && selectedFile.size > MAX_TRANSCRICAO_MB * 1024 * 1024) {
      setError(`Áudio/vídeo muito grande para transcrever (máx. ${MAX_TRANSCRICAO_MB} MB). Envie um trecho menor ou extraia só o áudio.`);
      return;
    }

    const timer = setInterval(() => {
      setUploadProgress(p => (p !== null && p < 85 ? p + 10 : p));
    }, 300);

    try {
      if (ehDocumentoTexto) {
        // Ingestão REAL: extrai o texto do documento e treina o RAG da Liz.
        const fd = new FormData();
        fd.append('arquivo', selectedFile);
        fd.append('titulo', fileForm.titulo);
        fd.append('descricao', fileForm.descricao);
        fd.append('categoria', fileForm.categoria);
        fd.append('palavras_chave', fileForm.palavras_chave);

        const resp = await fetch('/api/ia/knowledge-base/upload-document', {
          method: 'POST',
          headers: { Authorization: `Bearer ${getToken()}` },
          body: fd,
        });
        clearInterval(timer);
        setUploadProgress(100);
        if (resp.status === 401 || resp.status === 403) { setError('Sua sessão expirou. Faça login novamente.'); setUploadProgress(null); return; }
        if (!resp.ok) { const txt = await resp.text(); throw new Error(`Erro ${resp.status}: ${txt}`); }
        const data = await resp.json();
        const aviso = data.truncado ? ' (documento longo — a parte inicial foi indexada)' : '';
        setUploadSuccess(`✅ "${data.titulo}" — ${data.caracteresExtraidos} caracteres extraídos e já disponíveis para a Liz${aviso}!`);
        loadDocuments();
        loadStats();
        setTimeout(() => { setUploadProgress(null); setUploadSuccess(''); }, 6000);
      } else if (ehTranscrivel) {
        // Transcrição (Whisper): transcreve o áudio/vídeo e treina o RAG da Liz.
        const fd = new FormData();
        fd.append('arquivo', selectedFile);
        fd.append('titulo', fileForm.titulo);
        fd.append('descricao', fileForm.descricao);
        fd.append('categoria', fileForm.categoria);
        fd.append('palavras_chave', fileForm.palavras_chave);

        const resp = await fetch('/api/ia/knowledge-base/transcrever-e-ingerir', {
          method: 'POST',
          headers: { Authorization: `Bearer ${getToken()}` },
          body: fd,
        });
        clearInterval(timer);
        setUploadProgress(100);
        if (resp.status === 401 || resp.status === 403) { setError('Sua sessão expirou. Faça login novamente.'); setUploadProgress(null); return; }
        if (!resp.ok) { const txt = await resp.text(); throw new Error(`Erro ${resp.status}: ${txt}`); }
        const data = await resp.json();
        if (!data.success) {
          // Transcrição dormente (sem chave) ou saída vazia: informa sem travar.
          setUploadProgress(null);
          setError(data.aviso || 'Não foi possível transcrever este áudio/vídeo agora.');
          return;
        }
        setUploadSuccess(`✅ "${data.titulo}" transcrito — ${data.caracteresExtraidos} caracteres já disponíveis para a Liz!`);
        loadDocuments();
        loadStats();
        setTimeout(() => { setUploadProgress(null); setUploadSuccess(''); }, 6000);
      } else {
        // Outros formatos (ex.: vídeo .avi/.mov/.mkv, imagens): só ARMAZENA (não treina o RAG).
        const formData = new FormData();
        formData.append('arquivo', selectedFile);
        formData.append('titulo', fileForm.titulo);
        formData.append('descricao', fileForm.descricao);
        formData.append('tipo_material', fileForm.tipo_material);
        formData.append('audiencia', fileForm.audiencia);
        formData.append('tipo_arquivo', ext || 'bin');
        formData.append('nome_arquivo_original', selectedFile.name);
        if (fileForm.palavras_chave) {
          formData.append('palavras_chave', JSON.stringify(
            fileForm.palavras_chave.split(',').map(k => k.trim()).filter(Boolean)
          ));
        }
        const resp = await fetch('/api/ia/materiais/upload', {
          method: 'POST',
          headers: { Authorization: `Bearer ${getToken()}` },
          body: formData,
        });
        clearInterval(timer);
        setUploadProgress(100);
        if (!resp.ok) { const txt = await resp.text(); throw new Error(`Erro ${resp.status}: ${txt}`); }
        const data = await resp.json();
        setUploadSuccess(`✅ "${data.titulo || selectedFile.name}" armazenado. Obs.: este formato apenas fica guardado (não treina a Liz). Para treinar por voz, envie áudio .mp3/.wav/.m4a ou vídeo .mp4.`);
        setTimeout(() => { setUploadProgress(null); setUploadSuccess(''); }, 5000);
      }
      setSelectedFile(null);
      setFileForm({ titulo: '', descricao: '', tipo_material: 'treinamento', audiencia: 'interno', categoria: 'education', palavras_chave: '' });
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err: any) {
      clearInterval(timer);
      setUploadProgress(null);
      setError(err.message || 'Falha ao enviar arquivo');
    }
  };

  // ── Adicionar por link ──────────────────────────────────────
  const handleAdicionarLink = async () => {
    if (!linkForm.url_link.trim()) { setError('Informe a URL'); return; }
    if (!linkForm.titulo.trim()) { setError('Informe um título'); return; }
    setError('');
    try {
      const resp = await fetch('/api/ia/materiais/link', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ ...linkForm, nome_arquivo_original: linkForm.url_link, tipo_arquivo: 'link', caminho_arquivo: linkForm.url_link }),
      });
      if (!resp.ok) throw new Error(`Erro ${resp.status}`);
      setUploadSuccess('✅ Link adicionado para validação!');
      setLinkForm({ titulo: '', url_link: '', descricao: '', categoria: 'education' });
      setTimeout(() => setUploadSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message || 'Falha ao adicionar link');
    }
  };

  // ── Adicionar por vídeo ─────────────────────────────────────
  const handleAdicionarVideo = async () => {
    if (!videoForm.url_video.trim()) { setError('Informe a URL do vídeo'); return; }
    if (!videoForm.titulo.trim()) { setError('Informe um título'); return; }
    setError('');
    try {
      const resp = await fetch('/api/ia/materiais/video', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ ...videoForm, nome_arquivo_original: videoForm.url_video, tipo_arquivo: 'video', caminho_arquivo: videoForm.url_video }),
      });
      if (!resp.ok) throw new Error(`Erro ${resp.status}`);
      setUploadSuccess('✅ Vídeo adicionado para validação!');
      setVideoForm({ titulo: '', url_video: '', descricao: '', categoria: 'education' });
      setTimeout(() => setUploadSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message || 'Falha ao adicionar vídeo');
    }
  };

  // ── Upload texto (RAG) ──────────────────────────────────────
  const handleUploadDocument = async () => {
    if (!uploadForm.title || !uploadForm.sourceTitle || !uploadForm.sourceUrl) {
      setError('Preencha título, fonte e URL');
      return;
    }
    const newDoc = {
      id: `doc-${Date.now()}`,
      title: uploadForm.title,
      category: uploadForm.category,
      sourceTitle: uploadForm.sourceTitle,
      sourceUrl: uploadForm.sourceUrl,
      citationFormat: 'ABNT' as const,
      isOfficial: uploadForm.sourceUrl.includes('.gov.br') || uploadForm.sourceUrl.includes('.mec'),
      verifiedBy: 'admin@gestaocreche.com.br',
      verifiedAt: new Date().toISOString(),
      version: '1.0',
      lastUpdated: new Date().toISOString(),
      keywords: uploadForm.keywords.split(',').map(k => k.trim()),
      summary: uploadForm.summary,
      excerpt: uploadForm.summary.substring(0, 200),
      status: 'pending' as const,
    };
    try {
      const success = await ragService.addDocument(newDoc);
      if (success) {
        setUploadForm({ title: '', summary: '', sourceTitle: '', sourceUrl: '', category: 'law', keywords: '' });
        setShowAddPanel(false);
        await loadDocuments();
        await loadStats();
        setError('');
      }
    } catch (err) {
      setError('Erro ao adicionar documento');
    }
  };

  // ── Seed / Import ───────────────────────────────────────────
  const handleSeedInitial = async () => {
    if (!window.confirm('Importar os 5 documentos iniciais (LGPD, LDB, BNCC, Assinaturas SaaS, OWASP)?\n\nDocumentos com mesmo título são ignorados (sem duplicação).')) return;
    setLoading(true);
    try {
      const docsData = await import('../ia/knowledge-base/documents.json');
      const documentsToImport = (docsData as any).default?.documents || (docsData as any).documents || [];
      const resp = await fetch('/api/ia/knowledge-base/bulk-import', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ documents: documentsToImport, approveAll: true, markOfficial: true }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}: ${await resp.text()}`);
      const data = await resp.json();
      alert(`✅ Importação concluída!\n\nImportados: ${data.imported}\nIgnorados (já existiam): ${data.skipped}`);
      await loadDocuments(); await loadStats();
    } catch (err: any) {
      setError('Erro ao importar: ' + (err.message || err));
    } finally { setLoading(false); }
  };

  const handleSeedSystem = async () => {
    if (!window.confirm('Importar os documentos de conhecimento do SISTEMA (arquitetura, módulos, suporte e troubleshooting)?\n\nPermite que a LIZ responda perguntas técnicas e ajude a resolver problemas do próprio Gestão Creches.\n\nApós importar, clique em "Reindexar Embeddings".')) return;
    setLoading(true);
    try {
      const docsData = await import('../ia/knowledge-base/system-knowledge.json');
      const documentsToImport = (docsData as any).default?.documents || (docsData as any).documents || [];
      const resp = await fetch('/api/ia/knowledge-base/bulk-import', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ documents: documentsToImport, approveAll: true, markOfficial: true }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}: ${await resp.text()}`);
      const data = await resp.json();
      alert(`✅ Indexação concluída!\n\nImportados: ${data.imported}\nIgnorados: ${data.skipped}`);
      await loadDocuments(); await loadStats();
    } catch (err: any) {
      setError('Erro ao indexar: ' + (err.message || err));
    } finally { setLoading(false); }
  };

  // ── Reindexar embeddings ────────────────────────────────────
  // Necessário após importar documentos: o bulk-import apenas insere;
  // os embeddings (busca vetorial da LIZ) são gerados aqui.
  const handleReindex = async () => {
    if (!window.confirm('Reindexar os embeddings de toda a base de conhecimento?\n\nIsso torna os documentos importados pesquisáveis pela LIZ (busca vetorial). Pode levar alguns minutos.')) return;
    setLoading(true);
    try {
      const resp = await fetch('/api/ia/knowledge-base/reindex', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}: ${await resp.text()}`);
      const data = await resp.json();
      alert(`✅ Reindexação concluída!\n\nDocumentos processados: ${data.totalProcessados}/${data.totalDocumentos}`);
      await loadStats();
    } catch (err: any) {
      setError('Erro ao reindexar: ' + (err.message || err));
    } finally { setLoading(false); }
  };

  const filteredDocs = documents.filter(doc => {
    const matchesCategory = filterCategory === 'todos' || doc.category === filterCategory;
    const matchesSearch = doc.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         doc.summary.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="training-hub">
      {/* Header */}
      <div className="hub-header">
        <div className="header-content">
          <div className="header-icon"><BookOpen size={32} /></div>
          <div>
            <h2>Centro de Treinamento da Liz</h2>
            <p>Gerencie a base de conhecimento verificado para a assistente especialista</p>
          </div>
        </div>
      </div>

      {/* Status dos provedores de IA (créditos/saúde) */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 16, padding: 20, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
          <div>
            <strong style={{ fontSize: 15, color: '#111827' }}>⚡ Status da IA da Liz</strong>
            <div style={{ fontSize: 12, color: '#6b7280' }}>
              Saldo exato não é exposto pelas APIs — mostramos a saúde de cada provedor.
              {provedoresVerificadoEm ? ` Verificado às ${new Date(provedoresVerificadoEm).toLocaleTimeString('pt-BR')}.` : ''}
            </div>
          </div>
          <button
            onClick={verificarProvedores}
            disabled={verificandoProvedores}
            style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #d1d5db', background: verificandoProvedores ? '#f3f4f6' : '#fff', cursor: verificandoProvedores ? 'default' : 'pointer', fontSize: 13, color: '#374151' }}
          >
            {verificandoProvedores ? 'Verificando…' : '↻ Verificar agora'}
          </button>
        </div>

        {provedores.length === 0 ? (
          <div style={{ fontSize: 13, color: '#9ca3af' }}>Clique em "Verificar agora" para checar os provedores.</div>
        ) : (
          <div style={{ display: 'grid', gap: 10 }}>
            {provedores.map((p) => {
              const cor = p.status === 'ok'
                ? { dot: '#22c55e', bg: '#f0fdf4', border: '#bbf7d0', label: 'Operacional' }
                : p.status === 'depleted'
                ? { dot: '#f59e0b', bg: '#fffbeb', border: '#fde68a', label: 'Créditos esgotados' }
                : p.status === 'not_configured'
                ? { dot: '#9ca3af', bg: '#f9fafb', border: '#e5e7eb', label: 'Não configurado' }
                : { dot: '#ef4444', bg: '#fef2f2', border: '#fecaca', label: 'Com erro' };
              return (
                <div key={p.id} style={{ background: cor.bg, border: `1px solid ${cor.border}`, borderRadius: 10, padding: 12, display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                  <span style={{ width: 10, height: 10, borderRadius: '50%', background: cor.dot, marginTop: 5, flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <strong style={{ fontSize: 14, color: '#111827' }}>{p.nome}</strong>
                      <span style={{ fontSize: 11, fontWeight: 600, color: cor.dot }}>● {cor.label}</span>
                    </div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>{p.papel}</div>
                    <div style={{ fontSize: 12, color: '#374151', marginTop: 4 }}>{p.detalhe}</div>
                    {(p.status === 'depleted' || p.status === 'error') && (
                      <a href={p.recargaUrl} target="_blank" rel="noopener noreferrer"
                        style={{ display: 'inline-block', marginTop: 6, fontSize: 12, fontWeight: 600, color: '#2563eb' }}>
                        → Recarregar / gerenciar créditos
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Dossiê do Sistema — a Liz sempre atualizada sobre o próprio produto */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 16, padding: 20, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
          <div>
            <strong style={{ fontSize: 15, color: '#111827' }}>📘 Dossiê do Sistema</strong>
            <div style={{ fontSize: 12, color: '#6b7280' }}>
              O que a Liz sabe sobre o próprio produto. A parte factual é gerada a cada deploy — ela nunca
              busca isso na internet.
            </div>
          </div>
          <button
            onClick={ressincronizarDossie}
            disabled={dossieOcupado}
            style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #d1d5db', background: dossieOcupado ? '#f3f4f6' : '#fff', cursor: dossieOcupado ? 'default' : 'pointer', fontSize: 13, color: '#374151' }}
          >
            {dossieOcupado ? 'Processando…' : '↻ Republicar e reindexar'}
          </button>
        </div>

        {!dossie?.disponivel ? (
          <div style={{ fontSize: 13, color: '#9ca3af' }}>
            Dossiê ainda não gerado nesta versão do servidor — ele é criado no deploy. Se persistir, verifique o
            passo "Gerar Dossiê do Sistema" no CI.
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 12, color: '#374151', marginBottom: 12 }}>
              <span>Versão <strong>{dossie.versao}</strong></span>
              <span>{dossie.modulos.length} módulos ativos</span>
              <span>{dossie.entregas.length} entregas registradas</span>
              {dossie.ultimaEntregaEm && <span>Última entrega em <strong>{new Date(dossie.ultimaEntregaEm).toLocaleDateString('pt-BR')}</strong></span>}
              {dossie.publicadoEm && <span>Publicado na base em <strong>{new Date(dossie.publicadoEm).toLocaleDateString('pt-BR')}</strong></span>}
            </div>

            {dossie.entregas.length > 0 && (
              <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: 12, marginBottom: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Entregas mais recentes</div>
                <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: '#4b5563' }}>
                  {dossie.entregas.slice(0, 5).map((e, i) => (
                    <li key={i}>
                      {new Date(e.data).toLocaleDateString('pt-BR')} — [{e.area}] {e.titulo}
                      {e.pr ? ` (PR #${e.pr})` : ''}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
              Suas observações de produto
            </label>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>
              O que só você sabe: o que já está liberado para as creches, o que está em piloto e o que vem a seguir.
              Isso entra no mesmo documento que a Liz consulta.
            </div>
            <textarea
              value={notasDossie}
              onChange={(e) => setNotasDossie(e.target.value)}
              rows={6}
              placeholder={'Ex.: Portal dos Pais liberado para todas as creches assinantes desde julho.\nNFS-e ainda não disponível — aguardando definição do provedor.'}
              style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', resize: 'vertical' }}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8, flexWrap: 'wrap' }}>
              <button
                onClick={salvarNotasDossie}
                disabled={dossieOcupado}
                style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: '#ff4081', color: '#fff', fontWeight: 600, fontSize: 13, cursor: dossieOcupado ? 'default' : 'pointer', opacity: dossieOcupado ? 0.7 : 1 }}
              >
                Salvar observações
              </button>
              {dossieMsg && <span style={{ fontSize: 12, color: '#374151' }}>{dossieMsg}</span>}
            </div>
          </>
        )}
      </div>

      {/* Alerts */}
      {error && (
        <div className="alert alert-error">
          <AlertCircle size={18} />{error}
          <button onClick={() => setError('')}>×</button>
        </div>
      )}
      {uploadSuccess && (
        <div className="alert alert-success">
          <CheckCircle size={18} />{uploadSuccess}
        </div>
      )}

      {/* Statistics */}
      <div className="stats-grid">
        <div className="stat-card"><div className="stat-value">{stats.total}</div><div className="stat-label">Documentos Totais</div></div>
        <div className="stat-card"><div className="stat-value">{stats.official}</div><div className="stat-label">Fontes Oficiais</div></div>
        <div className="stat-card"><div className="stat-value">{stats.byCategory.law || 0}</div><div className="stat-label">Leis & Regulações</div></div>
        <div className="stat-card"><div className="stat-value">{stats.byCategory.education || 0}</div><div className="stat-label">Educação</div></div>
      </div>

      {/* Controls */}
      <div className="controls-bar">
        <div className="search-group">
          <input type="text" placeholder="Buscar documentos..." value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)} className="search-input" />
          <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)} className="filter-select">
            <option value="todos">Todas as categorias</option>
            {Object.entries(categoryLabels).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
        </div>
        <button className="btn-primary" onClick={() => { setShowAddPanel(!showAddPanel); setActiveTab('arquivo'); }}>
          <Plus size={18} /> Adicionar Material
        </button>
        <button className="btn-secondary" onClick={handleSeedInitial} style={{ marginLeft: 8 }}
          title="Importa LGPD, LDB, BNCC e mais">
          📚 Importar Base Inicial (5 docs)
        </button>
        <button className="btn-secondary" onClick={handleSeedSystem} style={{ marginLeft: 8 }}
          title="Indexa docs sobre arquitetura, módulos, suporte e troubleshooting do sistema">
          🖥️ Indexar Conhecimento do Sistema (16 docs)
        </button>
        <button className="btn-secondary" onClick={handleReindex} style={{ marginLeft: 8 }}
          title="Gera os embeddings (busca vetorial). Rode após importar documentos.">
          🔄 Reindexar Embeddings
        </button>
      </div>

      {/* ── PAINEL DE ADIÇÃO COM TABS ─────────────────────────────── */}
      {showAddPanel && (
        <div className="upload-form" style={{ padding: 0, overflow: 'hidden' }}>
          {/* Tabs */}
          <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>
            {([
              { key: 'contrato', icon: <FileText size={15} />, label: '📄 Modelo de Contrato' },
              { key: 'arquivo', icon: <File size={15} />, label: 'Arquivo do computador' },
              { key: 'link',    icon: <Link size={15} />, label: 'Link / URL' },
              { key: 'video',   icon: <Video size={15} />, label: 'Vídeo (YouTube / URL)' },
              { key: 'texto',   icon: <FileText size={15} />, label: 'Texto livre (RAG)' },
            ] as { key: UploadTab; icon: React.ReactNode; label: string }[]).map(tab => (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '12px 18px', border: 'none', cursor: 'pointer',
                  fontWeight: activeTab === tab.key ? 700 : 400,
                  fontSize: 13,
                  borderBottom: activeTab === tab.key ? '2px solid #ff4081' : '2px solid transparent',
                  background: 'transparent',
                  color: activeTab === tab.key ? '#ff4081' : '#666',
                  transition: 'all 0.15s',
                }}>
                {tab.icon}{tab.label}
              </button>
            ))}
            <button onClick={() => setShowAddPanel(false)}
              style={{ marginLeft: 'auto', padding: '12px 16px', background: 'transparent', border: 'none', cursor: 'pointer', color: '#999', fontSize: 18 }}>
              ×
            </button>
          </div>

          <div style={{ padding: '20px 24px' }}>

            {/* ── TAB: MODELO DE CONTRATO ────────────────────────────── */}
            {activeTab === 'contrato' && (
              <div>
                <p style={{ color: '#555', fontSize: 13, marginBottom: 16 }}>
                  Cole o <strong>texto completo</strong> de um modelo de contrato. Ele entra na base
                  jurídica da Liz já <strong>aprovado</strong> e disponível para consulta. Se o Gemini
                  estiver sem crédito, o modelo é salvo mesmo assim e passa a ser indexado
                  semanticamente assim que o crédito voltar (via "Reindexar").
                </p>

                <div className="liz-form-grid">
                  <input type="text" placeholder="Nome do modelo * (ex.: Contrato de Prestação de Serviços Educacionais — Padrão)"
                    value={contratoForm.titulo}
                    onChange={(e) => setContratoForm(f => ({ ...f, titulo: e.target.value }))} />

                  <input type="text" placeholder="Descrição curta (opcional — quando usar este modelo)"
                    value={contratoForm.descricao}
                    onChange={(e) => setContratoForm(f => ({ ...f, descricao: e.target.value }))} />

                  <textarea placeholder="Cole aqui o TEXTO COMPLETO do contrato (cláusulas, anexos, etc.) *"
                    rows={12}
                    value={contratoForm.texto}
                    onChange={(e) => setContratoForm(f => ({ ...f, texto: e.target.value }))}
                    style={{ fontFamily: 'inherit', lineHeight: 1.5 }} />

                  <input type="text" placeholder="Palavras-chave (separadas por vírgula: mensalidade, rescisão, matrícula...)"
                    value={contratoForm.palavrasChave}
                    onChange={(e) => setContratoForm(f => ({ ...f, palavrasChave: e.target.value }))} />
                </div>

                <button
                  onClick={handleAdicionarModeloContrato}
                  disabled={salvandoContrato}
                  style={{ marginTop: 14, padding: '12px 20px', borderRadius: 8, border: 'none',
                    background: salvandoContrato ? '#f3a5c0' : '#ff4081', color: '#fff',
                    fontWeight: 700, fontSize: 14, cursor: salvandoContrato ? 'default' : 'pointer' }}>
                  {salvandoContrato ? 'Salvando…' : '📄 Adicionar modelo à base da Liz'}
                </button>
              </div>
            )}

            {/* ── TAB: ARQUIVO ───────────────────────────────────────── */}
            {activeTab === 'arquivo' && (
              <div>
                <p style={{ color: '#555', fontSize: 13, marginBottom: 16 }}>
                  Envie PDFs, documentos Word, PowerPoint, textos, áudios ou vídeos salvos no seu computador.
                  Documentos treinam a Liz pelo texto; áudios e vídeos são <strong>transcritos automaticamente</strong> e o texto entra na base.
                  Tamanho máximo: <strong>{MAX_FILE_MB} MB</strong> (áudio/vídeo para transcrição: até <strong>{MAX_TRANSCRICAO_MB} MB</strong>).
                </p>

                {/* Drop zone */}
                <div
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={handleFileDrop}
                  onClick={() => fileInputRef.current?.click()}
                  style={{
                    border: `2px dashed ${selectedFile ? '#43a047' : '#ff4081'}`,
                    borderRadius: 10,
                    padding: '28px 20px',
                    textAlign: 'center',
                    cursor: 'pointer',
                    background: selectedFile ? '#f1f8e9' : '#fff5f8',
                    marginBottom: 16,
                    transition: 'all 0.2s',
                  }}>
                  <input ref={fileInputRef} type="file" accept={ACCEPTED_FILES}
                    onChange={handleFileSelect} style={{ display: 'none' }} />
                  {selectedFile ? (
                    <div>
                      <CheckCircle size={32} color="#43a047" style={{ marginBottom: 8 }} />
                      <div style={{ fontWeight: 700, color: '#333' }}>{selectedFile.name}</div>
                      <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                        {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                        &nbsp;·&nbsp;
                        <span style={{ color: '#ff4081', cursor: 'pointer' }}
                          onClick={(e) => { e.stopPropagation(); setSelectedFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}>
                          Trocar arquivo
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <Upload size={32} color="#ff4081" style={{ marginBottom: 8 }} />
                      <div style={{ fontWeight: 600, color: '#333' }}>Arraste o arquivo aqui ou clique para selecionar</div>
                      <div style={{ fontSize: 12, color: '#888', marginTop: 6 }}>
                        PDF · DOCX · PPTX · TXT · MD · MP3 · WAV · M4A · MP4 · MOV
                      </div>
                    </div>
                  )}
                </div>

                <div className="liz-form-grid">
                  <input type="text" placeholder="Título do material *"
                    value={fileForm.titulo}
                    onChange={(e) => setFileForm(f => ({ ...f, titulo: e.target.value }))} />
                  <textarea placeholder="Descrição (opcional)" rows={2}
                    value={fileForm.descricao}
                    onChange={(e) => setFileForm(f => ({ ...f, descricao: e.target.value }))} />
                  <select value={fileForm.tipo_material}
                    onChange={(e) => setFileForm(f => ({ ...f, tipo_material: e.target.value }))}>
                    <option value="treinamento">📚 Material de Treinamento</option>
                    <option value="legislacao">⚖️ Legislação / Normativa</option>
                    <option value="procedimento">📋 Procedimento Interno</option>
                    <option value="pedagogico">🎓 Material Pedagógico</option>
                    <option value="marketing">📊 Marketing / Apresentação</option>
                    <option value="video">🎬 Vídeo Educacional</option>
                  </select>
                  <select value={fileForm.audiencia}
                    onChange={(e) => setFileForm(f => ({ ...f, audiencia: e.target.value }))}>
                    <option value="interno">🔒 Interno (apenas equipe)</option>
                    <option value="publico">🌐 Público</option>
                  </select>
                  <input type="text" placeholder="Palavras-chave (separadas por vírgula)"
                    value={fileForm.palavras_chave}
                    onChange={(e) => setFileForm(f => ({ ...f, palavras_chave: e.target.value }))} />
                </div>

                {/* Barra de progresso */}
                {uploadProgress !== null && (
                  <div style={{ margin: '12px 0' }}>
                    <div style={{ height: 8, background: '#eee', borderRadius: 4, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${uploadProgress}%`, background: '#ff4081', transition: 'width 0.3s' }} />
                    </div>
                    <div style={{ fontSize: 12, color: '#888', marginTop: 4, textAlign: 'right' }}>{uploadProgress}%</div>
                  </div>
                )}

                <div className="form-actions">
                  <button className="btn-primary" onClick={handleUploadArquivo} disabled={!selectedFile || uploadProgress !== null}>
                    <Upload size={18} />
                    {uploadProgress !== null ? 'Enviando...' : 'Enviar Arquivo'}
                  </button>
                  <button className="btn-secondary" onClick={() => setShowAddPanel(false)}>Cancelar</button>
                </div>
              </div>
            )}

            {/* ── TAB: LINK ──────────────────────────────────────────── */}
            {activeTab === 'link' && (
              <div>
                <p style={{ color: '#555', fontSize: 13, marginBottom: 16 }}>
                  Adicione um link para um documento em <strong>Google Drive</strong>, Dropbox, SharePoint,
                  site oficial ou qualquer URL pública. A LIZ registrará a referência para uso no treinamento.
                </p>
                <div className="liz-form-grid">
                  <input type="text" placeholder="Título do documento *"
                    value={linkForm.titulo} onChange={(e) => setLinkForm(f => ({ ...f, titulo: e.target.value }))} />
                  <input type="url" placeholder="URL completa (https://...) *"
                    value={linkForm.url_link} onChange={(e) => setLinkForm(f => ({ ...f, url_link: e.target.value }))} />
                  <textarea placeholder="Descrição (opcional)" rows={2}
                    value={linkForm.descricao} onChange={(e) => setLinkForm(f => ({ ...f, descricao: e.target.value }))} />
                  <select value={linkForm.categoria} onChange={(e) => setLinkForm(f => ({ ...f, categoria: e.target.value }))}>
                    {Object.entries(categoryLabels).map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                </div>
                <div style={{ background: '#fffbe6', border: '1px solid #ffe082', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#7a6000', marginBottom: 16 }}>
                  💡 <strong>Google Drive:</strong> Compartilhe o arquivo como "Qualquer pessoa com o link pode visualizar" antes de copiar a URL.
                </div>
                <div className="form-actions">
                  <button className="btn-primary" onClick={handleAdicionarLink}>
                    <Link size={18} /> Adicionar Link
                  </button>
                  <button className="btn-secondary" onClick={() => setShowAddPanel(false)}>Cancelar</button>
                </div>
              </div>
            )}

            {/* ── TAB: VÍDEO ─────────────────────────────────────────── */}
            {activeTab === 'video' && (
              <div>
                <p style={{ color: '#555', fontSize: 13, marginBottom: 16 }}>
                  Adicione um vídeo do <strong>YouTube</strong>, Vimeo, Google Drive ou qualquer URL de vídeo.
                  A LIZ registrará o material para referência no treinamento.
                </p>
                <div className="liz-form-grid">
                  <input type="text" placeholder="Título do vídeo *"
                    value={videoForm.titulo} onChange={(e) => setVideoForm(f => ({ ...f, titulo: e.target.value }))} />
                  <input type="url" placeholder="URL do vídeo (https://youtube.com/...) *"
                    value={videoForm.url_video} onChange={(e) => setVideoForm(f => ({ ...f, url_video: e.target.value }))} />
                  <textarea placeholder="Descrição (opcional)" rows={2}
                    value={videoForm.descricao} onChange={(e) => setVideoForm(f => ({ ...f, descricao: e.target.value }))} />
                  <select value={videoForm.categoria} onChange={(e) => setVideoForm(f => ({ ...f, categoria: e.target.value }))}>
                    {Object.entries(categoryLabels).map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-actions">
                  <button className="btn-primary" onClick={handleAdicionarVideo}>
                    <Video size={18} /> Adicionar Vídeo
                  </button>
                  <button className="btn-secondary" onClick={() => setShowAddPanel(false)}>Cancelar</button>
                </div>
              </div>
            )}

            {/* ── TAB: TEXTO (RAG) ───────────────────────────────────── */}
            {activeTab === 'texto' && (
              <div>
                <p style={{ color: '#555', fontSize: 13, marginBottom: 16 }}>
                  Adicione um documento de texto diretamente à base RAG da LIZ. Ideal para legislação,
                  normas e conteúdo que já está em formato digital.
                </p>
                <div className="liz-form-grid">
                  <input type="text" placeholder="Título do documento *"
                    value={uploadForm.title} onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })} />
                  <input type="text" placeholder="Fonte (ex: Lei 13.709/2018) *"
                    value={uploadForm.sourceTitle} onChange={(e) => setUploadForm({ ...uploadForm, sourceTitle: e.target.value })} />
                  <input type="url" placeholder="URL da fonte (ex: https://www.planalto.gov.br/...) *"
                    value={uploadForm.sourceUrl} onChange={(e) => setUploadForm({ ...uploadForm, sourceUrl: e.target.value })} />
                  <select value={uploadForm.category} onChange={(e) => setUploadForm({ ...uploadForm, category: e.target.value as any })}>
                    {Object.entries(categoryLabels).map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                  <textarea placeholder="Resumo / Descrição do conteúdo"
                    value={uploadForm.summary} onChange={(e) => setUploadForm({ ...uploadForm, summary: e.target.value })} rows={3} />
                  <input type="text" placeholder="Palavras-chave (separadas por vírgula)"
                    value={uploadForm.keywords} onChange={(e) => setUploadForm({ ...uploadForm, keywords: e.target.value })} />
                </div>
                <div className="form-actions">
                  <button className="btn-primary" onClick={handleUploadDocument}>
                    <Upload size={18} /> Enviar para Validação
                  </button>
                  <button className="btn-secondary" onClick={() => setShowAddPanel(false)}>Cancelar</button>
                </div>
              </div>
            )}

          </div>
        </div>
      )}

      {/* Documents List */}
      <div className="documents-section">
        <h3>Documentos na Base de Conhecimento ({filteredDocs.length})</h3>

        {loading ? (
          <p className="loading">Carregando documentos...</p>
        ) : filteredDocs.length === 0 ? (
          <p className="no-results">Nenhum documento encontrado</p>
        ) : (
          <div className="documents-list">
            {filteredDocs.map((doc) => (
              <div key={doc.id} className={`doc-card status-${doc.status}`}>
                <div className="doc-header">
                  <div className="doc-info">
                    <div className="doc-title">
                      <FileText size={18} />{doc.title}
                    </div>
                    <div className="doc-meta">
                      <span className={`category-badge ${doc.category}`}>
                        {categoryLabels[doc.category]}
                      </span>
                      <span className={`status-badge ${doc.status}`}>
                        {doc.status === 'approved' && <CheckCircle size={12} />}
                        {doc.status === 'pending' && <AlertCircle size={12} />}
                        {doc.status}
                      </span>
                    </div>
                  </div>
                  <div className="doc-actions">
                    <button className="btn-icon"
                      onClick={() => setSelectedDoc(selectedDoc?.id === doc.id ? null : doc)}
                      title="Visualizar detalhes"><Eye size={18} /></button>
                    <button className="btn-icon danger" title="Remover documento"><Trash2 size={18} /></button>
                  </div>
                </div>

                <p className="doc-summary">{doc.summary}</p>

                <div className="doc-footer">
                  <a href={doc.sourceUrl} target="_blank" rel="noopener noreferrer" className="source-link">
                    🔗 {doc.sourceTitle}
                  </a>
                  <div className="doc-keywords">
                    {(() => {
                      const kws: string[] = Array.isArray(doc.keywords)
                        ? doc.keywords
                        : (typeof doc.keywords === 'string'
                            ? (() => { try { return JSON.parse(doc.keywords); } catch { return []; } })()
                            : []);
                      return (
                        <>
                          {kws.slice(0, 3).map((kw) => (
                            <span key={kw} className="keyword-tag">{kw}</span>
                          ))}
                          {kws.length > 3 && <span className="keyword-tag">+{kws.length - 3}</span>}
                        </>
                      );
                    })()}
                  </div>
                </div>

                {selectedDoc?.id === doc.id && (
                  <div className="doc-details">
                    <div className="detail-row"><strong>Verificado por:</strong> {doc.verifiedBy}</div>
                    <div className="detail-row"><strong>Data de Verificação:</strong> {new Date(doc.verifiedAt).toLocaleDateString('pt-BR')}</div>
                    <div className="detail-row"><strong>Fonte:</strong> {doc.sourceUrl}</div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Training Guide */}
      <div className="training-guide">
        <h3>📚 Guia de Treinamento</h3>
        <div className="guide-content">
          <p><strong>Como adicionar materiais para a LIZ:</strong></p>
          <ol>
            <li>Clique em <strong>"+ Adicionar Material"</strong> acima</li>
            <li>Escolha a aba conforme o tipo: <em>Arquivo do computador</em>, <em>Link/URL</em>, <em>Vídeo</em> ou <em>Texto livre</em></li>
            <li>Preencha o título e as informações adicionais</li>
            <li>O material entra em <em>validação</em> — aprove-o para que a LIZ o utilize</li>
          </ol>
          <p><strong>Formatos aceitos para upload:</strong></p>
          <ul>
            <li>📄 Documentos: PDF, Word (.docx), PowerPoint (.pptx), Texto (.txt, .md)</li>
            <li>🎬 Vídeos: MP4, AVI, MOV, MKV (ou link YouTube/Vimeo)</li>
            <li>🔗 Links: Google Drive, Dropbox, SharePoint, sites oficiais</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
